<template>
  <button
    type="button"
    class="btn btn-subtle-warning btn-sm me-2 flex-shrink-0"
    @click="adminModal?.toggleModal()"
  >
    <font-awesome-icon icon="fa-solid fa-user-shield" />
  </button>
  <ModalComponent
    ref="adminModal"
    submitButtonText="Add Company"
    :submit="confirmAddCompanies"
    :submitDisabled="!newCompanyIds.trim()"
  >
    <template #title>
      <font-awesome-icon
        icon="fa-solid fa-user-shield"
        class="text-warning me-2"
      />
      {{ $t("dashboard.add_company_access") }}
    </template>
    <template #body>
      <p class="text-body-secondary mb-3">
        Grant this user access to additional companies by entering their
        database IDs below. The page will reload after companies are added.
      </p>
      <div class="alert alert-subtle-warning p-3 mb-0">
        <div class="form-group">
          <label class="form-label fw-semibold small"> Company IDs </label>
          <input
            type="text"
            v-model="newCompanyIds"
            class="form-control"
            :class="{ 'is-invalid': validationError }"
            placeholder="e.g. 123 or 123, 456, 789"
            @input="validationError = ''"
          />
          <div v-if="validationError" class="invalid-feedback">
            {{ validationError }}
          </div>
          <small class="form-text text-muted">
            Enter a single ID or multiple comma-separated IDs
          </small>
        </div>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Account } from "@/types";
import { ref, useTemplateRef } from "vue";

// Staff-only affordance (rendered only for admin sessions) to grant the
// managed account access to more companies. Shared by every portal sidenav.
const props = defineProps<{
  accountData: Account;
}>();

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const adminModal =
  useTemplateRef<InstanceType<typeof ModalComponent>>("adminModal");
const newCompanyIds = ref<string>("");
const validationError = ref<string>("");

const confirmAddCompanies = () => {
  if (!newCompanyIds.value) {
    return;
  }
  const tokens = newCompanyIds.value
    .split(",")
    .map((id) => id.trim())
    .filter((id) => id !== "");

  // SECURITY: reject the whole submission on any malformed token — parseInt
  // would silently truncate "12abc" to 12 and grant access to the wrong
  // company.
  if (
    tokens.length === 0 ||
    tokens.some((token) => !/^[1-9]\d*$/.test(token))
  ) {
    validationError.value = "Please enter valid company ID(s)";
    return;
  }
  validationError.value = "";
  const companyIds = tokens.map((token) => parseInt(token, 10));

  const confirmed = window.confirm(
    `Are you sure you want to add ${companyIds.length} company/companies with IDs: ${companyIds.join(", ")}?`
  );
  if (confirmed) {
    addCompaniesToAccount(companyIds);
  }
};

const addCompaniesToAccount = async (company_ids: number[]) => {
  try {
    let successCount = 0;
    let failCount = 0;

    for (const company_id of company_ids) {
      try {
        const response = await goSolidApi.post(
          `/api/admin/accounts/${props.accountData.id}/add-company/${company_id}`,
          {}
        );
        if (response.status === 200) {
          successCount++;
        } else {
          failCount++;
          console.error(
            `Failed to add company ${company_id}. Status: ${response.status}`
          );
        }
      } catch (error: unknown) {
        failCount++;
        console.error(`Error adding company ${company_id}:`, error);
      }
    }

    if (successCount > 0) {
      globalSuccessHandler(
        "Companies Added",
        `Successfully added ${successCount} company/companies. Failed: ${failCount}`
      );
      window.location.reload();
    } else {
      throw new Error(`Failed to add all ${failCount} companies`);
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
};
</script>
